import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styles from './AugAlert.module.css';
import Icon from '../icons/Icons';
import { Button } from '../buttons/Button';

/**
 * AugAlert
 * Reusable alert component with 5 variants and optional link and icon.
 *
 * Props:
 * - type: 'default' | 'discovery' | 'success' | 'warning' | 'error'
 * - title?: string | ReactNode
 * - text?: string | ReactNode
 * - icon?: ReactNode (if omitted, a default icon per type is shown)
 * - link?: { label: string, href?: string, onClick?: (e) => void, target?: string, rel?: string }
 * - errorMessages?: string[] (only used/visible for type='error')
 * - className?: string
 * - style?: React.CSSProperties
 */
export function AugAlert({
  type = 'default',
  title,
  text,
  icon,
  link,
  errorMessages,
  className,
  style,
  dismissible = false,
  onDismiss,
  ...rest
}) {
  const [visible, setVisible] = useState(true);
  if (dismissible && !visible) return null;
  const variantClass = styles[type] || styles.default;
  const classes = [styles.alert, variantClass];
  if (className) classes.push(className);

  const DefaultIcon = () => {
    // Heroicons-style minimal inline SVGs to avoid external deps
    switch (type) {
      case 'success':
        return (
            <Icon name="user_actions/check_circle" color="var(--system-green-600)" height="26" width="26"/>
        );
      case 'warning':
        return (
          <Icon name="communicate/warning" color="var(--system-amber-800)" height="26" width="26"/>
        );
      case 'error':
        return (
          <Icon name="communicate/error" color="var(--system-danger-600)" height="26" width="26"/>
        );
      case 'discovery':
        return (
          <Icon name="comunicate/help" color="var(--brand-as-purple-600)" height="26" width="26"/>
        );
      case 'default':
      default:
        return (
          <Icon name="comunicate/info" color="var(--system-sky-600)" height="26" width="26"/>
        );
    }
  };

  const renderLink = () => {
    if (!link || !link.label) return null;
    if (link.onClick) {
      // Map alert types to button colors
      const getButtonProps = (alertType) => {
        switch (alertType) {
          case 'success':
            return { color: 'var(--brand-as-green-600)', textColor: 'var(--brand-as-green-100)' };
          case 'warning':
            return { color: 'var(--system-amber-600)', textColor: 'var(--system-amber-100)'};
          case 'error':
            return { color: 'var(--system-red-600)', textColor: 'var(--system-red-100)' };
          case 'discovery':
            return { color: 'var(--system-sky-600)', textColor: 'var(--system-sky-100)' };
          case 'default':
          default:
            return { color: 'var(--brand-as-blue-600)', textColor: 'var(--brand-as-blue-100)' };
        }
      };

      const buttonProps = getButtonProps(type);
      
      return (
        <Button
          variant="primary"
          size="sm"
          color={buttonProps.color}
          textColor={buttonProps.textColor}
          onClick={link.onClick}
          disabled={link.disabled}
          iconLeft={{ name: 'user_actions/check_circle', color: 'currentColor', width: 24, height: 24 }}
        >
          {link.label}
        </Button>
      );
    }
    return (
      <a className={styles.link} href={link.href || '#'} target={link.target} rel={link.rel}>
        {link.label}
      </a>
    );
  };

  const mergedStyle = dismissible ? { position: 'relative', paddingRight: 36, ...style } : style;

  return (
    <div className={classes.join(' ')} style={mergedStyle} role="alert" {...rest}>
      {dismissible && (
        <button
          type="button"
          aria-label="Dismiss"
          onClick={() => { setVisible(false); if (onDismiss) onDismiss(); }}
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            border: 'none',
            background: 'transparent',
            color: 'inherit',
            cursor: 'pointer',
            lineHeight: 1,
            padding: 4,
          }}
        >
          ×
        </button>
      )}
      <div className={styles.leading}>
        {icon || <DefaultIcon />}
      </div>
      <div className={styles.body}>
        {title && <div className={styles.title}>{title}</div>}
        {text && <div className={styles.text}>{text}</div>}
        {type === 'error' && Array.isArray(errorMessages) && errorMessages.length > 0 && (
          <ul className={styles.errors}>
            {errorMessages.map((msg, idx) => (
              <li key={idx}>{msg}</li>
            ))}
          </ul>
        )}
        {renderLink()}
      </div>
    </div>
  );
}

AugAlert.propTypes = {
  type: PropTypes.oneOf(['default', 'discovery', 'success', 'warning', 'error']),
  title: PropTypes.node,
  text: PropTypes.node,
  icon: PropTypes.node,
  link: PropTypes.shape({
    label: PropTypes.node.isRequired,
    href: PropTypes.string,
    onClick: PropTypes.func,
    target: PropTypes.string,
    rel: PropTypes.string,
  }),
  errorMessages: PropTypes.arrayOf(PropTypes.string),
  className: PropTypes.string,
  style: PropTypes.object,
  dismissible: PropTypes.bool,
  onDismiss: PropTypes.func,
};

export default AugAlert;
